import {Button} from '@primer/react'
import {useCallback, useRef} from 'react'

import {DockedPanel, type ManageBatchEntrySurface, useIsDockedPanelOpen} from '../../contexts/DockedPanelContext'
import {useOpenManageBatchPanel} from '../../hooks/use-open-manage-batch-panel'

interface ManageBatchHeaderButtonProps {
  count: number
  size?: 'small' | 'medium' | 'large'
  className?: string
  entrySurface: ManageBatchEntrySurface
}

/**
 * Green "Manage batch (N)" pill that replaces ReviewMenuButton in PR headers
 * when the unified batch is non-empty.
 *
 * Uses `openPanel` (not `togglePanel`) so clicking the button always opens or
 * brings the panel forward — dismissal is via the panel's own close button or
 * the panel switcher.
 */
export function ManageBatchHeaderButton({
  count,
  size = 'medium',
  className,
  entrySurface,
}: ManageBatchHeaderButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const isPanelOpen = useIsDockedPanelOpen(DockedPanel.ManageBatchReviewComments)
  const openManageBatchPanel = useOpenManageBatchPanel(entrySurface)

  const handleClick = useCallback(() => {
    openManageBatchPanel({returnFocusTo: buttonRef.current})
  }, [openManageBatchPanel])

  return (
    <Button
      ref={buttonRef}
      type="button"
      variant="primary"
      size={size}
      count={count}
      className={className}
      onClick={handleClick}
      aria-expanded={isPanelOpen}
      data-testid="manage-batch-header-button"
    >
      Manage batch
    </Button>
  )
}
